import React, { useEffect, useRef } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';

interface PaginationControlsProps {
  currentPage: number;
  totalPages: number;
  handlePageChange: (page: number) => void;
  onHeightChange?: (height: number) => void; // Add this prop
}

const PaginationControls: React.FC<PaginationControlsProps> = ({
  currentPage,
  totalPages,
  handlePageChange,
  onHeightChange, // Add this prop
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current && onHeightChange) {
      onHeightChange(containerRef.current.clientHeight);
    }
  }, [onHeightChange, currentPage, totalPages]);

  // Add keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        // Don't trigger when user is typing in an input field
        return;
      }

      if (e.key === 'a' && currentPage > 1) {
        e.preventDefault();
        handlePageChange(currentPage - 1);
      } else if (e.key === 'd' && currentPage < totalPages) {
        e.preventDefault();
        handlePageChange(currentPage + 1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentPage, totalPages, handlePageChange]);

  return (
    <div
      ref={containerRef}
      className="flex justify-end items-center p-4 mt-auto mb-2"
    >
      <button
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage <= 1}
        className={`${
          currentPage > 1
            ? 'opacity-100'
            : 'bg-grey opacity-30 cursor-not-allowed'
        } 
        bg-violet size-8 rounded flex justify-center items-center  hover:brightness-90`}
      >
        <ChevronLeftIcon className="w-4 h-4" />
      </button>
      <div className="flex flex-row justify-center text-center px-2">
        <span className="w-20 text-center truncate whitespace-nowrap">{`${currentPage} / ${totalPages}`}</span>
      </div>
      <button
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage >= totalPages}
        className={`${
          currentPage < totalPages
            ? 'opacity-100'
            : 'g-grey opacity-30 cursor-not-allowed'
        } 
        bg-violet size-8 rounded flex justify-center items-center hover:brightness-90`}
      >
        <ChevronRightIcon className="w-4 h-4" />
      </button>
    </div>
  );
};

export default PaginationControls;
